/**
 * Project details (D2 · FMS feedback 2026-08-11, recap #7) — the supervisor's
 * one-way directives/description for a project, backed by `internal_notes`.
 *
 * This is distinct from the requester's *intake notes* (what they asked for)
 * and from *chat* (a two-way thread): it's how Leo tells the crew HOW to run
 * the job — dock access, sequence, gotchas — and the task checklist below reads
 * as the detail beneath it. Supervisors/admins edit it here; the crew read it
 * (read-only) on their own project screen. ~50–60% of projects use it, so an
 * empty field stays quiet: a compact "Add details" affordance, not a big blank
 * box.
 */
import { useEffect, useState } from 'react';
import { ClipboardList, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Project } from '@vari-crew/schemas';
import { useUpdateProject } from '@/api/projects';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Input';

export function ProjectDetailsCard({ project }: { project: Project }) {
  const update = useUpdateProject(project.id);
  const saved = project.internal_notes ?? '';
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(saved);

  // Re-seed when the server value changes (e.g. another edit lands) — but not
  // while the supervisor is mid-edit, so we never clobber their draft.
  useEffect(() => {
    if (!editing) setValue(saved);
  }, [saved, editing]);

  const dirty = value.trim() !== saved.trim();

  function save() {
    if (!dirty) {
      setEditing(false);
      return;
    }
    update.mutate(
      { internal_notes: value.trim() || null },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Project details saved');
        },
        onError: (e) => toast.error(`Could not save: ${e.message}`),
      },
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-ink-faint" /> Project details
        </CardTitle>
        {!editing && saved && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-2">
            <p className="text-[11px] text-ink-faint">
              Directions for the crew — dock access, sequence, site quirks. They see this on their
              project screen. Not shown to the requester.
            </p>
            <Textarea
              autoFocus
              rows={5}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. Use the freight elevator on the north side. Stage everything in the lobby first, then install top-down — 4th floor before 3rd."
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={update.isPending || !dirty}>
                {update.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save details
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setValue(saved);
                  setEditing(false);
                }}
                disabled={update.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : saved ? (
          <p className="text-sm whitespace-pre-wrap">{saved}</p>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="text-sm text-ink-faint hover:text-ink flex items-center gap-1.5"
          >
            <ClipboardList className="w-3.5 h-3.5" />
            Add details — how the crew should run this job.
          </button>
        )}
      </CardContent>
    </Card>
  );
}
